// White text keeps 4.5:1 on a fill up to this WCAG relative luminance.
const WHITE_TEXT_MAX_LUMINANCE = 0.183;

/** True when white text would not read on `color` (`hsl(...)` or `#rrggbb`).
 *  A user keeps the color they were first given, and older ones can be light. */
export function needsDarkText(color: string): boolean {
  const rgb = parseRgb(color.trim());
  return !!rgb && luminance(rgb) > WHITE_TEXT_MAX_LUMINANCE;
}

/** The user's own hue, a step darker than `color` and dark enough for white
 *  text at 4.5:1: a control sitting on a fill of that color. */
export function darkerShade(color: string): string | null {
  const rgb = parseRgb(color.trim());
  if (!rgb) return null;
  let keep = 0.8;
  while (
    keep > 0.2 &&
    luminance(rgb.map((c) => c * keep)) > WHITE_TEXT_MAX_LUMINANCE
  ) {
    keep -= 0.05;
  }
  // Mixing with black in srgb scales each channel by `keep`, as computed above.
  return `color-mix(in srgb, ${color} ${Math.round(keep * 100)}%, black)`;
}

function luminance(rgb: number[]): number {
  const linear = (c: number) =>
    c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  const [r, g, b] = rgb.map(linear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function parseRgb(color: string): [number, number, number] | null {
  const hex = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(color);
  if (hex) {
    return [hex[1], hex[2], hex[3]].map((v) => parseInt(v, 16) / 255) as [
      number,
      number,
      number,
    ];
  }
  const hsl = /^hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%/i.exec(
    color,
  );
  if (!hsl) return null;
  const h = Number(hsl[1]);
  const s = Number(hsl[2]) / 100;
  const l = Number(hsl[3]) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const sector = Math.floor((((h % 360) + 360) % 360) / 60);
  const [r, g, b] = [
    [c, x, 0],
    [x, c, 0],
    [0, c, x],
    [0, x, c],
    [x, 0, c],
    [c, 0, x],
  ][sector];
  return [r + m, g + m, b + m];
}
